import React, { useState } from 'react'
import { opcionesDeporte } from '../../../../lib/deportesOrg'
import { Search, Filter, ChevronDown, ChevronUp, Trash2, Package, Download, MoreHorizontal, Pencil } from 'lucide-react'
import {
  Button, Badge, Card, CardContent, Input, Label,
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../../../ui'
import { actualizarAsignacion, eliminarAsignacion } from '../../../../lib/supabase'
import { useConfirmDialog } from '../../../../hooks/useConfirmDialog'
import { useUnsavedGuard } from '../../../../hooks/useUnsavedGuard'
import { formatDate } from '../../../../utils/helpers'
import { FiltroInscritos, AvisoSinPadron } from '../../FiltroInscritos'
import XlsxPopulate from 'xlsx-populate/browser/xlsx-populate'
import { estadoDePago } from '../../../../lib/uniformesEstadoPago'
import { jugadorEnGrupo, playerDeportes, entregaPasaFiltros } from './entregaHelpers'
import { ctxDeEntrega, etapaUniforme, puedeEntregar, ETAPA_LABEL, ETAPA_BADGE, numeroEfectivo, agruparPorGrupo } from './uniformesControlHelpers'

const ListaEntregas = ({
  matriz, grupos, orgDeportes, staffById = {},
  onChanged, loading, inscritosEstado, entregasDelPadron,
}) => {
  const { confirm, alert, ConfirmDialog } = useConfirmDialog()
  const [searchTerm, setSearchTerm] = useState('')
  const [showFilters, setShowFilters] = useState(false)
  const [deporteFilter, setDeporteFilter] = useState('todos')
  const [grupoFilter, setGrupoFilter] = useState('todos')
  const [pagadoFilter, setPagadoFilter] = useState('todos')
  const [origenFilter, setOrigenFilter] = useState('todos')
  const [etapaFilter, setEtapaFilter] = useState('todos')
  const [showTotales, setShowTotales] = useState(false)
  const [expandedId, setExpandedId] = useState(null)

  const [editModal, setEditModal] = useState({ open: false, row: null })
  const editGuard = useUnsavedGuard({ onClose: () => setEditModal({ open: false, row: null }) })
  const [editForm, setEditForm] = useState({ talla: '', estatus: 'no_entregado' })
  const [editSaving, setEditSaving] = useState(false)

  const jugadorById = Object.fromEntries((matriz.jugadores || []).map((j) => [j.id, j]))
  const prendaById = Object.fromEntries((matriz.prendas || []).map((p) => [p.id, p]))

  const deporteOptions = [
    { value: 'todos', label: 'Todos' },
    ...opcionesDeporte(orgDeportes),
  ]

  const rows = (entregasDelPadron || matriz.entregas || [])
    .map((e) => ({ entrega: e, jugador: jugadorById[e.jugador_id], prenda: prendaById[e.prenda_id] }))
    .filter((r) => r.jugador && r.prenda)
    .filter((r) => {
      if (searchTerm) {
        const t = searchTerm.toLowerCase()
        if (!r.jugador.nombre_completo?.toLowerCase().includes(t) && !String(numeroEfectivo(r.entrega, r.jugador) ?? '').includes(t)) return false
      }
      if (deporteFilter !== 'todos') {
        if (r.prenda.deporte !== 'todos' && !playerDeportes(r.jugador).includes(deporteFilter)) return false
      }
      if (grupoFilter !== 'todos' && !jugadorEnGrupo(r.jugador, grupoFilter)) return false
      if (!entregaPasaFiltros(r.entrega, { pagado: pagadoFilter, origen: origenFilter })) return false
      if (etapaFilter !== 'todos' && etapaUniforme(r.entrega, ctxDeEntrega(r.entrega)) !== etapaFilter) return false
      return true
    })
    .sort((a, b) => (a.jugador.nombre_completo || '').localeCompare(b.jugador.nombre_completo || ''))

  const hayFiltros = deporteFilter !== 'todos' || grupoFilter !== 'todos' || pagadoFilter !== 'todos' || origenFilter !== 'todos' || etapaFilter !== 'todos'

  const grupoNombreById = Object.fromEntries((grupos || []).map((g) => [g.id, g.nombre_del_grupo]))
  const totalesPorGrupo = agruparPorGrupo(rows.map((r) => r.entrega))

  const openEdit = (row) => {
    if (!row.entrega.asignacionId) {
      alert('Sin registro de entrega', 'Esta prenda del plan aún no tiene talla asignada. Captúrala desde "Tallas y entrega".')
      return
    }
    setEditModal({ open: true, row })
    setEditForm({ talla: row.entrega.talla || '', estatus: row.entrega.estatus || 'no_entregado' })
    editGuard.resetDirty()
  }

  const handleEditSave = async () => {
    if (!editForm.talla || !editModal.row) return
    if (editForm.estatus === 'entregado' && !puedeEntregar(editModal.row.entrega, ctxDeEntrega(editModal.row.entrega))) {
      await alert('No recibido', 'Esta prenda aún no se ha recibido del proveedor. Regístrala como recibida en Pedidos antes de entregar.')
      return
    }
    setEditSaving(true)
    const { error } = await actualizarAsignacion(editModal.row.entrega.asignacionId, { talla: editForm.talla, estatus: editForm.estatus })
    setEditSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo guardar'); return }
    editGuard.resetDirty()
    setEditModal({ open: false, row: null })
    onChanged()
  }

  const handleEditDelete = async () => {
    if (!editModal.row) return
    if (!await confirm('Quitar entrega', 'Se cancelará el cargo vinculado si existe y no está pagado. ¿Continuar?', { destructive: true })) return
    setEditSaving(true)
    const { error } = await eliminarAsignacion(editModal.row.entrega.asignacionId)
    setEditSaving(false)
    if (error) { await alert('Error', error.message || 'No se pudo eliminar'); return }
    editGuard.resetDirty()
    setEditModal({ open: false, row: null })
    onChanged()
  }

  const handleExportExcel = async () => {
    const workbook = await XlsxPopulate.fromBlankAsync()
    const sheet = workbook.sheet(0).name('Entregas')
    const headers = ['#', 'Jugador', 'Grupo', 'Prenda', 'Origen', 'Talla', 'Pago', 'Estado', 'Entregada el', 'Entregada por']
    headers.forEach((h, i) => {
      sheet.cell(1, i + 1).value(h).style({ bold: true, fill: { type: 'solid', color: '4472C4' }, fontColor: 'FFFFFF', fontSize: 11 })
    })
    rows.forEach((r, idx) => {
      const row = idx + 2
      const etapa = etapaUniforme(r.entrega, ctxDeEntrega(r.entrega))
      sheet.cell(row, 1).value(numeroEfectivo(r.entrega, r.jugador) ?? '')
      sheet.cell(row, 2).value(r.jugador.nombre_completo || '')
      sheet.cell(row, 3).value(grupoNombreById[r.entrega.grupo_id] || 'Sin grupo')
      sheet.cell(row, 4).value(r.prenda.nombre || '')
      sheet.cell(row, 5).value(r.entrega.origen === 'plan' ? 'Plan' : 'Extra')
      sheet.cell(row, 6).value(r.entrega.talla || '—')
      sheet.cell(row, 7).value(estadoDePago(r.entrega).label)
      sheet.cell(row, 8).value(ETAPA_LABEL[etapa] || etapa)
      sheet.cell(row, 9).value(r.entrega.entregado_at ? formatDate(r.entrega.entregado_at) : '')
      sheet.cell(row, 10).value(staffById[r.entrega.entregado_por] || '')
    })
    sheet.column(1).width(6); sheet.column(2).width(26); sheet.column(3).width(18)
    sheet.column(4).width(20); sheet.column(5).width(10); sheet.column(6).width(10)
    sheet.column(7).width(12); sheet.column(8).width(16); sheet.column(9).width(14); sheet.column(10).width(20)
    const blob = await workbook.outputAsync()
    const url = URL.createObjectURL(new Blob([blob]))
    const a = document.createElement('a')
    a.href = url
    a.download = `entregas_uniformes_${new Date().toISOString().split('T')[0]}.xlsx`
    document.body.appendChild(a); a.click(); document.body.removeChild(a)
    URL.revokeObjectURL(url)
  }

  return (
    <div>
      {inscritosEstado?.sinPadron && (
        <div className="mb-3"><AvisoSinPadron estado={inscritosEstado} /></div>
      )}

      <div className="mb-4 flex items-center gap-2 flex-nowrap">
        <div className="relative flex-1 min-w-0">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Buscar jugador..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
        </div>
        <span className="text-sm text-muted-foreground hidden lg:block whitespace-nowrap">
          <span className="font-medium text-foreground">{rows.length}</span> entregas
        </span>
        <Button variant="outline" onClick={() => setShowFilters((s) => !s)} className="relative h-10 w-10 p-0 sm:h-10 sm:w-auto sm:px-3 shrink-0">
          <Filter className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Filtros</span>
          {showFilters ? <ChevronUp className="ml-1 hidden h-4 w-4 sm:inline" /> : <ChevronDown className="ml-1 hidden h-4 w-4 sm:inline" />}
          {hayFiltros && <span className="absolute -top-1 -right-1 h-2 w-2 rounded-full bg-primary" />}
        </Button>
        {rows.length > 0 && (
          <Button variant="outline" onClick={handleExportExcel} className="h-10 w-10 p-0 shrink-0" aria-label="Exportar a Excel" title="Exportar a Excel">
            <Download className="h-4 w-4" />
          </Button>
        )}
      </div>

      {showFilters && (
        <div className="mb-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <div className="space-y-1">
            <Label className="text-xs">Padrón</Label>
            <FiltroInscritos estado={inscritosEstado} id="le-ver-no-inscritos" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Grupo</Label>
            <Select value={grupoFilter} onValueChange={setGrupoFilter}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                {grupos.map((g) => <SelectItem key={g.id} value={g.id}>{g.nombre_del_grupo}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Deporte</Label>
            <Select value={deporteFilter} onValueChange={setDeporteFilter}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{deporteOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Prenda del / extra</Label>
            <Select value={origenFilter} onValueChange={setOrigenFilter}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="plan">Del plan</SelectItem>
                <SelectItem value="extra">Extra</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Pago</Label>
            <Select value={pagadoFilter} onValueChange={setPagadoFilter}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="pagado">Pagado</SelectItem>
                <SelectItem value="sin_pagar">Sin pagar</SelectItem>
                <SelectItem value="sin_cobro">Sin cobro propio</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Estado</Label>
            <Select value={etapaFilter} onValueChange={setEtapaFilter}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="por_pedir">{ETAPA_LABEL.por_pedir}</SelectItem>
                <SelectItem value="en_pedido">{ETAPA_LABEL.en_pedido}</SelectItem>
                <SelectItem value="pedido">{ETAPA_LABEL.pedido}</SelectItem>
                <SelectItem value="recibido">{ETAPA_LABEL.recibido}</SelectItem>
                <SelectItem value="entregado">{ETAPA_LABEL.entregado}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {hayFiltros && (
            <div className="flex items-end">
              <Button variant="ghost" size="sm" onClick={() => { setDeporteFilter('todos'); setGrupoFilter('todos'); setPagadoFilter('todos'); setOrigenFilter('todos'); setEtapaFilter('todos') }}>Limpiar filtros</Button>
            </div>
          )}
        </div>
      )}

      {!loading && rows.length > 0 && (
        <Card className="mb-4">
          <Button
            type="button"
            variant="ghost"
            onClick={() => setShowTotales((s) => !s)}
            aria-expanded={showTotales}
            className="flex h-auto w-full items-center justify-between rounded-none px-4 py-3 text-sm font-medium"
          >
            <span>Totales por grupo</span>
            {showTotales ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>
          {showTotales && (
            <CardContent className="pt-0">
              <div className="space-y-2">
                {totalesPorGrupo.map((g) => (
                  <div key={g.grupoId} className="flex flex-wrap items-center justify-between gap-2 border-t pt-2 text-sm first:border-t-0 first:pt-0">
                    <span className="font-medium">{g.grupoId === 'sin_grupo' ? 'Sin grupo' : (grupoNombreById[g.grupoId] || 'Sin grupo')}</span>
                    <span className="text-xs text-muted-foreground">
                      {g.total} total · {g.porPedir} por pedir · {g.enPedido} en pedido · {g.pedido} pedido · {g.recibido} recibido · {g.entregado} entregado · {g.sinCobrar} sin cobrar
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {loading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Package className="mx-auto mb-3 h-12 w-12 opacity-30" />
            <p>{hayFiltros || searchTerm ? 'Ninguna entrega coincide con los filtros.' : 'Aún no hay entregas en esta temporada.'}</p>
            <p className="mt-1 text-xs">Usa "Tallas y entrega" para capturar tallas y entregar prendas.</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          {/* Movil: lista, no tabla. Los nombres de jugador son texto libre y una
              tabla de 6 columnas se recorta en silencio bajo el overflow-x-hidden
              de FeaturedLayout (regla 11). Tocar la fila expande el resto. */}
          <div className="sm:hidden divide-y px-4">
            {rows.map((r) => {
              const ctx = ctxDeEntrega(r.entrega)
              const etapa = etapaUniforme(r.entrega, ctx)
              const numero = numeroEfectivo(r.entrega, r.jugador)
              const abierto = expandedId === r.entrega.id
              const meta = [r.prenda.nombre, r.entrega.talla || 'Sin talla'].join(' · ')

              return (
                <div key={r.entrega.id} className="py-3">
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      onClick={() => setExpandedId(abierto ? null : r.entrega.id)}
                      className="flex min-w-0 flex-1 items-start gap-2 text-left"
                      aria-expanded={abierto}
                    >
                      <ChevronDown className={`h-4 w-4 mt-0.5 shrink-0 text-muted-foreground transition-transform ${abierto ? 'rotate-180' : ''}`} />
                      <span className="min-w-0">
                        <span className="block font-medium text-sm truncate">
                          {r.jugador.nombre_completo}
                          {numero != null && <Badge variant="outline" className="ml-2 text-[10px]">#{numero}</Badge>}
                        </span>
                        <span className="block text-xs text-muted-foreground truncate">{meta}</span>
                      </span>
                    </button>
                    <div className="flex items-start gap-1 shrink-0">
                      <Badge variant={ETAPA_BADGE[etapa]}>{ETAPA_LABEL[etapa]}</Badge>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0 -mt-0.5" title="Más acciones">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openEdit(r)}>
                            <Pencil className="h-4 w-4" /> Editar entrega
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* Detalle: exactamente lo mismo que las columnas de escritorio */}
                  {abierto && (
                    <dl className="mt-3 ml-6 space-y-2 text-xs border-l pl-3">
                      <div className="flex items-baseline justify-between gap-2">
                        <dt className="text-muted-foreground">Prenda</dt>
                        <dd className="text-right">{r.prenda.nombre}</dd>
                      </div>
                      <div className="flex items-baseline justify-between gap-2">
                        <dt className="text-muted-foreground">Origen</dt>
                        <dd>
                          <Badge variant={r.entrega.origen === 'plan' ? 'info' : 'outline'}>
                            {r.entrega.origen === 'plan' ? 'Plan' : 'Extra'}
                          </Badge>
                        </dd>
                      </div>
                      <div className="flex items-baseline justify-between gap-2">
                        <dt className="text-muted-foreground">Talla</dt>
                        <dd className="text-right">{r.entrega.talla || '—'}</dd>
                      </div>
                      <div className="flex items-baseline justify-between gap-2">
                        <dt className="text-muted-foreground">Pago</dt>
                        <dd>
                          <Badge variant={estadoDePago(r.entrega).variant}>
                            {estadoDePago(r.entrega).label}
                          </Badge>
                        </dd>
                      </div>
                      <div className="flex items-baseline justify-between gap-2">
                        <dt className="text-muted-foreground">Estado</dt>
                        <dd><Badge variant={ETAPA_BADGE[etapa]}>{ETAPA_LABEL[etapa]}</Badge></dd>
                      </div>
                      {r.entrega.entregado_at && (
                        <div className="pt-1 border-t border-dashed">
                          <dd className="text-muted-foreground leading-tight">
                            Entregada el {formatDate(r.entrega.entregado_at)}
                            {staffById[r.entrega.entregado_por] ? ` por ${staffById[r.entrega.entregado_por]}` : ''}
                          </dd>
                        </div>
                      )}
                    </dl>
                  )}
                </div>
              )
            })}
          </div>

          {/* Escritorio: la tabla completa */}
          <div className="hidden sm:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Jugador</TableHead>
                  <TableHead className="hidden md:table-cell">Prenda</TableHead>
                  <TableHead className="hidden sm:table-cell">Origen</TableHead>
                  <TableHead>Talla</TableHead>
                  <TableHead className="hidden sm:table-cell">Pago</TableHead>
                  <TableHead>Estado</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => {
                  const ctx = ctxDeEntrega(r.entrega)
                  const etapa = etapaUniforme(r.entrega, ctx)
                  const numero = numeroEfectivo(r.entrega, r.jugador)
                  return (
                    <TableRow key={r.entrega.id} className="cursor-pointer" onClick={() => openEdit(r)}>
                      <TableCell className="font-medium">
                        {r.jugador.nombre_completo}
                        {numero != null && <Badge variant="outline" className="ml-2 text-[10px]">#{numero}</Badge>}
                      </TableCell>
                      <TableCell className="hidden md:table-cell">{r.prenda.nombre}</TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge variant={r.entrega.origen === 'plan' ? 'info' : 'outline'}>
                          {r.entrega.origen === 'plan' ? 'Plan' : 'Extra'}
                        </Badge>
                      </TableCell>
                      <TableCell>{r.entrega.talla || <span className="text-muted-foreground">—</span>}</TableCell>
                      <TableCell className="hidden sm:table-cell">
                        <Badge variant={estadoDePago(r.entrega).variant}>
                          {estadoDePago(r.entrega).label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={ETAPA_BADGE[etapa]}>{ETAPA_LABEL[etapa]}</Badge>
                        {r.entrega.entregado_at && (
                          <p className="mt-0.5 text-[10px] text-muted-foreground">
                            {formatDate(r.entrega.entregado_at)}
                            {staffById[r.entrega.entregado_por] ? ` · ${staffById[r.entrega.entregado_por]}` : ''}
                          </p>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      <Dialog open={editModal.open} onOpenChange={editGuard.onOpenChange}>
        <DialogContent {...editGuard.dirtyProps} className="sm:max-w-sm">
          <DialogHeader>
            <DialogTitle>{editModal.row?.prenda?.nombre} — {editModal.row?.jugador?.nombre_completo}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Talla</Label>
              <div className="flex flex-wrap gap-2">
                {(editModal.row?.prenda?.tallas || []).map((t) => (
                  <Button
                    key={t}
                    type="button"
                    size="sm"
                    variant={editForm.talla === t ? 'default' : 'outline'}
                    aria-pressed={editForm.talla === t}
                    onClick={() => { editGuard.markDirty(); setEditForm((p) => ({ ...p, talla: t })) }}
                  >
                    {t}
                  </Button>
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <Label>Estatus de entrega</Label>
              <Select value={editForm.estatus} onValueChange={(v) => { editGuard.markDirty(); setEditForm((p) => ({ ...p, estatus: v })) }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="no_entregado">No entregado</SelectItem>
                  <SelectItem value="pendiente">Pendiente</SelectItem>
                  <SelectItem
                    value="entregado"
                    disabled={!!editModal.row && !puedeEntregar(editModal.row.entrega, ctxDeEntrega(editModal.row.entrega))}
                  >
                    Entregado
                  </SelectItem>
                </SelectContent>
              </Select>
              {editModal.row && !puedeEntregar(editModal.row.entrega, ctxDeEntrega(editModal.row.entrega)) && (
                <p className="text-xs text-muted-foreground">Aún no se ha recibido del proveedor.</p>
              )}
              {editModal.row?.entrega?.entregado_at && (
                <p className="text-xs text-muted-foreground">
                  Entregada el {formatDate(editModal.row.entrega.entregado_at)}
                  {staffById[editModal.row.entrega.entregado_por] ? ` por ${staffById[editModal.row.entrega.entregado_por]}` : ''}
                </p>
              )}
            </div>
          </div>
          <DialogFooter className="flex items-center justify-between sm:justify-between">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="icon" className="h-9 w-9" aria-label="Más acciones" title="Más acciones" disabled={editSaving}>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start">
                <DropdownMenuItem onClick={handleEditDelete} className="text-destructive focus:text-destructive">
                  <Trash2 className="h-4 w-4" /> Quitar prenda del jugador
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => editGuard.onOpenChange(false)}>Cancelar</Button>
              <Button onClick={handleEditSave} disabled={editSaving || !editForm.talla}>{editSaving ? 'Guardando...' : 'Guardar'}</Button>
            </div>
          </DialogFooter>
          {editGuard.DiscardDialog}
        </DialogContent>
      </Dialog>
      <ConfirmDialog />
    </div>
  )
}

export default ListaEntregas
